// ─── Share ──────────────────────────────────────────────────────────────────
// Reuses src/game/shareReceipt.ts verbatim for the emoji string. Renders a
// spoiler-free card to a canvas, shares it via the Web Share API (files), and
// falls back to copying the emoji receipt to the clipboard.

import { generateShareReceipt } from '@game/shareReceipt';
import type { GameResult, ShareReceipt } from '@gametypes';
import { ACCENT, SITE_URL } from './config';

export function buildReceipt(result: GameResult, themeName: string): ShareReceipt {
  return generateShareReceipt(result, themeName);
}

/** Emoji receipt text minus the internal UTM link — what people paste in chats. */
export function receiptText(r: ShareReceipt): string {
  return r.emojiGrid
    .split('\n')
    .filter((l) => !l.startsWith('http'))
    .concat([`play free — ${SITE_URL.replace(/^https?:\/\//, '')}`])
    .join('\n');
}

function drawCard(r: ShareReceipt, accentColor: string): HTMLCanvasElement {
  const W = 1080;
  const H = 1080;
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d')!;

  ctx.fillStyle = '#0D0D1A';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(108,99,255,0.10)';
  ctx.fillRect(0, 0, W, 12);

  ctx.textAlign = 'center';
  ctx.fillStyle = accentColor;
  ctx.font = '700 68px system-ui, -apple-system, Segoe UI, sans-serif';
  ctx.fillText(r.themeName, W / 2, 150);

  ctx.fillStyle = '#8A88A6';
  ctx.font = '400 34px system-ui, sans-serif';
  ctx.fillText(r.challengeDate, W / 2, 205);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 120px system-ui, sans-serif';
  ctx.fillText(r.score.toLocaleString(), W / 2, 360);

  ctx.fillStyle = '#B8B6C8';
  ctx.font = '500 40px system-ui, sans-serif';
  ctx.fillText(
    `${r.chainsCleared} chains   ·   ${r.chainsCleared > 0 ? r.beatRate + '% on beat' : '—'}`,
    W / 2,
    430,
  );

  ctx.fillStyle = accentColor;
  ctx.font = '600 44px system-ui, sans-serif';
  ctx.fillText(r.bestMoment, W / 2, 520);

  // emoji grid
  const rows = r.emojiGrid
    .split('\n')
    .filter((l) => /^[🟩🟨⬛]+$/u.test(l));
  ctx.font = '64px system-ui, sans-serif';
  ctx.fillStyle = '#FFFFFF';
  rows.forEach((row, i) => ctx.fillText(row, W / 2, 640 + i * 78));

  ctx.fillStyle = '#6C6988';
  ctx.font = '500 36px system-ui, sans-serif';
  ctx.fillText(`play free — ${SITE_URL.replace(/^https?:\/\//, '')}`, W / 2, H - 70);

  return cv;
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

export async function shareReceipt(
  r: ShareReceipt,
  accentColor: string = ACCENT,
): Promise<ShareOutcome> {
  const text = receiptText(r);
  try {
    const cv = drawCard(r, accentColor);
    const blob: Blob | null = await new Promise((res) => cv.toBlob(res, 'image/png'));
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
    };
    if (blob && nav.share && nav.canShare) {
      const file = new File([blob], `sortbeat-${r.challengeDate}.png`, { type: 'image/png' });
      const data: ShareData = { files: [file], text };
      if (nav.canShare(data)) {
        try {
          await nav.share(data);
          return 'shared';
        } catch (err) {
          if ((err as DOMException)?.name === 'AbortError') return 'cancelled';
          // fall through to clipboard
        }
      }
    }
  } catch {
    /* fall through */
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
